import type { AppContext } from '@application/context/AppContext';
import { ActivationService } from '@infra/licence/ActivationService';
import { OfflineLicenceImportService } from '@infra/licence/OfflineLicenceImportService';
import { assertActivationAllowed } from '@application/licence/ActivationInstallGuard';
import { ClockTamperGuard } from '@infra/licence/ClockTamperGuard';
import { LicenceRenewalService } from '@infra/licence/LicenceRenewalService';
import { LicenceStatusService } from '@infra/licence/LicenceStatusService';
import { LicenceRuntime } from '@infra/licence/LicenceRuntime';
import { CabinetPortfolioService } from '@application/licence/CabinetPortfolioService';
import { NexxusShell } from '@presentation/shell/NexxusShell';
import { setDeviceLabel } from '@infra/persistence/DeviceLabelStore';
import { ToastService } from '@presentation/components/Toast';
import { logger } from '@infra/telemetry/Logger';
import { AuthService } from '@infra/auth/AuthService';
import type { ActivationState } from '@infra/licence/ActivationClaims';
import { clearAuthStateForActivation } from '@application/auth/authBridge';
import {
  notifyLicenceUpdated,
  syncLicenceEntitlements,
} from '@application/licence/LicenceEntitlementService';
import {
  applyCloudWebEntitlementsJwt,
  applyCloudWebInstallation,
  registerCloudWebEntitlementsListener,
  registerCloudWebInstallationListener,
} from '@application/licence/CloudWebEntitlements';
import {
  CLOUD_LICENCE_REFUSAL_EVENT,
  cloudLicenceMessage,
  isCloudRestrictionCode,
} from '@application/licence/CloudLicenceMessages';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { CLOUD_SESSION_EXPIRED_EVENT } from '@infra/api/BusinessApiClient';
import { purgeCloudBrowserTenantData } from '@infra/persistence/cloudBrowserReset';

export async function bootstrapLicence(
  root: HTMLElement,
  ctx: AppContext,
): Promise<LicenceRuntime> {
  const runtime = new LicenceRuntime();

  // Poste de bureau : un redémarrage rouvre le dossier du cabinet (ses comptes y sont), pas l'entreprise cliente
  // ouverte en dernier.
  if (!isBrowserCloudHost()) {
    try {
      await CabinetPortfolioService.reopenCabinetHome();
    } catch (err) {
      logger.warn({
        event: 'cabinet_reopen_home_failed',
        err: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (import.meta.env.DEV && !window.electron) {
    logger.warn({ event: 'licence_dev_browser_bypass' });
    ctx.setLicenceRuntime(runtime.withDevBypass());
    return runtime;
  }

  if (isBrowserCloudHost()) {
    logger.info({ event: 'licence_cloud_web_session' });
    // Aucun droit tant que l'API n'a pas livré un jeton `entitlements` signé (après connexion).
    ctx.setLicenceRuntime(runtime.withCloudWebPending());
    registerCloudWebEntitlementsListener(async (jwt, expectedTenantId) => {
      const applied = await applyCloudWebEntitlementsJwt(runtime, jwt, expectedTenantId);
      if (applied) notifyLicenceUpdated();
      return applied;
    });
    registerCloudWebInstallationListener((info, expectedTenantId) => {
      const applied = applyCloudWebInstallation(runtime, info, expectedTenantId);
      if (applied) notifyLicenceUpdated();
      return applied;
    });
    // Refus de licence rencontré en cours de session (révoquée, suspendue, expirée) : lecture seule.
    window.addEventListener(CLOUD_LICENCE_REFUSAL_EVENT, (event) => {
      const code = (event as CustomEvent<{ code?: string }>).detail?.code;
      if (!isCloudRestrictionCode(code)) return;
      const message = cloudLicenceMessage(code) ?? '';
      runtime.setRestriction({
        kind: code === 'license_revoked' ? 'revoked' : 'suspended',
        message,
        since: new Date().toISOString(),
      });
      ToastService.warning(message);
    });
    // Session perdue en cours d'utilisation (expirée, révoquée, cookie effacé) : retour à la connexion.
    let sessionLostHandled = false;
    window.addEventListener(CLOUD_SESSION_EXPIRED_EVENT, () => {
      if (sessionLostHandled) return;
      sessionLostHandled = true;
      AuthService.markSessionExpiredNotice();
      ctx.auth.logout();
      // Session perdue : l'entreprise ne doit pas rester lisible dans le navigateur (puis retour à la connexion).
      void purgeCloudBrowserTenantData().finally(() => {
        window.location.reload();
      });
    });
    return runtime;
  }

  const clock = await ClockTamperGuard.observeStartup();
  if (clock.suspicious && clock.message) {
    ToastService.warning(clock.message);
  }

  let state = await runtime.loadFromStore();
  if (!state.valid) {
    state = await promptActivation(root, runtime, ctx);
  }

  if (!state.valid) {
    logger.error({ event: 'licence_blocked', errorCode: state.errorCode });
    throw new Error('Activation licence requise');
  }

  ctx.setLicenceRuntime(runtime);
  // Révocation / suspension / hors ligne prolongé : lecture seule + message explicite (ADR-003).
  LicenceStatusService.setNotifier((restriction) => ToastService.warning(restriction.message));
  await LicenceStatusService.restore(runtime);
  const restriction = runtime.getRestriction();
  if (restriction) ToastService.warning(restriction.message);
  await syncLicenceEntitlements(ctx, runtime);
  LicenceStatusService.watch(runtime);
  LicenceRenewalService.watchEntitlementSync(runtime, ctx);
  LicenceRenewalService.schedule(runtime, ctx);
  logger.info({ event: 'licence_validated', dataMode: runtime.dataMode });
  return runtime;
}

function promptActivation(
  root: HTMLElement,
  runtime: LicenceRuntime,
  ctx: AppContext,
): Promise<ActivationState> {
  const activation = new ActivationService();
  return new Promise<ActivationState>((resolve) => {
    NexxusShell.renderLicenseGate(root, async (commercialKey, deviceLabel) => {
      if (deviceLabel) {
        await setDeviceLabel(deviceLabel);
      }
      const result = await activation.activate(commercialKey, (claims) =>
        assertActivationAllowed(ctx, claims),
      );
      if (!result.ok) {
        return result.error ?? 'Activation échouée';
      }
      const state = await runtime.loadFromStore();
      await clearAuthStateForActivation(ctx);
      resolve(state);
      return null;
    }, async (fileText) => {
      // Poste sans Internet : fichier de licence signé, vérifié localement (aucun appel réseau).
      const result = await OfflineLicenceImportService.importFromText(fileText, {
        beforePersist: (claims) => assertActivationAllowed(ctx, claims),
      });
      if (!result.ok) return result.message;
      const state = await runtime.loadFromStore();
      await clearAuthStateForActivation(ctx);
      resolve(state);
      return null;
    });
  });
}
